/**
 * What the phone's and the iPad's Assets page and its From Assets picker both draw: a file's tile and the groups of
 * folders. Ported from the confirmed prototype (`pg-as.js`); every class starts with `sa-`.
 */
import type { ReactNode } from "react";
import type { LibraryAsset, LibraryFolder } from "../../shared/types";
import { GroupedList, ListRow } from "../components/season/GroupedList";
import { cx } from "../components/ui/cx";
import { Icon, type IconName } from "../components/ui/Icon";
import type { GroupKey } from "./fold";
import { ALL, countIn, groupFolders, groupKids, lenLabel } from "./library";
import { folderIconName, GroupMark, Media } from "./parts";
import type { useRailFold } from "./useRailFold";

export const TRASH = "trash";

/** A file's tile: its picture with the length on it, its name and one quiet line. A tick circle makes it a choice. */
export function Tile({
  a,
  sub,
  on = false,
  tick = false,
  up,
  onClick,
}: {
  a: LibraryAsset;
  sub: string;
  on?: boolean;
  tick?: boolean;
  /** The file is still going up: what it says, and how far it is. */
  up?: { line: string; pct: number; fail?: boolean };
  onClick?: () => void;
}) {
  const media = (
    <span className="sa-m">
      {up ? (
        <span className="sa-bar">
          <i style={{ width: `${up.pct}%` }} />
        </span>
      ) : (
        <Media a={a} />
      )}
      {a.duration_sec != null && !up ? <span className="sa-dur">{lenLabel(a.duration_sec)}</span> : null}
      {tick ? (
        <span className="sa-ck">
          <Icon name="check" />
        </span>
      ) : null}
    </span>
  );
  const caption = (
    <span className="sa-c">
      <b>{a.name}</b>
      <span>{up ? up.line : sub}</span>
    </span>
  );
  if (up) {
    return (
      <div className={cx("sa-t", "sa-up", up.fail && "sa-fail")} role="status">
        {media}
        {caption}
      </div>
    );
  }
  return (
    <button type="button" className={cx("sa-t", on && "sa-on")} aria-pressed={tick ? on : undefined} aria-current={!tick && on ? "true" : undefined} onClick={onClick}>
      {media}
      {caption}
    </button>
  );
}

export function Grid({ children }: { children: ReactNode }) {
  return <div className="sa-grid">{children}</div>;
}

/** Why the room, notes and guests differ from a project's plain folder: they say what is in them. */
function iconOf(f: LibraryFolder): IconName {
  if (f.system !== "recordings") return folderIconName(f);
  if (f.id === "room/notes") return "mic";
  if (f.id === "room/guests" || f.parent_id === "room/guests") return "users";
  return "play";
}

function Row({ icon, name, count, on, depth = 0, onOpen }: { icon: IconName; name: string; count: number | null; on: boolean; depth?: number; onOpen: () => void }) {
  if (!depth) return <ListRow icon={icon} title={name} value={count === null ? undefined : String(count)} on={on} onClick={onOpen} innerProps={on ? { "aria-current": "true" } : undefined} />;
  return (
    <li className={cx("k-lr", on && "k-on", "sa-kid")}>
      <button type="button" className="k-lr-b" aria-current={on ? "true" : undefined} onClick={onOpen}>
        <span className="k-ico">
          <Icon name={icon} />
        </span>
        <span className="k-lr-t">
          <b>{name}</b>
        </span>
        {count === null ? null : <span className="k-lr-v">{count}</span>}
        <Icon name="chev" />
      </button>
    </li>
  );
}

interface GroupsProps {
  folders: LibraryFolder[];
  assets: LibraryAsset[];
  /** The place that is open: ALL, a folder's id or TRASH. Null lights nothing. */
  open: string | null;
  fold: ReturnType<typeof useRailFold>;
  onOpen: (id: string) => void;
  /** The Trash row under All files, with how many files are in it. */
  trash?: { count: number; onOpen: () => void };
  onNewFolder?: () => void;
  /** What a place holds; the picker passes what fits it. */
  count?: (id: string) => number;
  /** The picker leaves out a place that holds nothing. */
  hideEmpty?: boolean;
  /** True while the library cannot be read: no count is said. */
  blind?: boolean;
}

/**
 * All files and Trash, then FOLDERS (the person's own tree), FLASHKUT (a folder per project) and FLASHROOM (a folder per
 * recorded room, then Notes you recorded and Sent by guests, which unfolds into a folder per guest). Each group folds.
 * The product groups are read only: no menu, no Move to, no New folder.
 */
export function Groups({ folders, assets, open, fold, onOpen, trash, onNewFolder, count, hideEmpty = false, blind = false }: GroupsProps) {
  const n = (id: string) => (count ? count(id) : countIn(folders, assets, id));
  const said = (id: string) => (blind ? null : n(id));
  const shows = (id: string) => !hideEmpty || n(id) > 0;
  const row = (f: LibraryFolder, depth = 0) => <Row key={f.id} icon={iconOf(f)} name={f.name} count={said(f.id)} on={open === f.id} depth={depth} onOpen={() => onOpen(f.id)} />;
  const tree = (parent: string | null, depth: number): ReactNode[] =>
    fold.own.filter((f) => f.parent_id === parent && shows(f.id)).flatMap((f) => [row(f, depth), ...tree(f.id, depth + 1)]);
  const roomRows = (f: LibraryFolder): ReactNode[] => {
    const kids = groupKids(folders, f.id).filter((k) => shows(k.id));
    if (!groupKids(folders, f.id).length) return [row(f)];
    const unfolded = fold.fold.folders.includes(f.id);
    return [
      <li key={f.id} className={cx("k-lr", open === f.id && "k-on")}>
        <div className="sa-gr">
          <button type="button" className="k-lr-b" aria-current={open === f.id ? "true" : undefined} onClick={() => onOpen(f.id)}>
            <span className="k-ico">
              <Icon name={iconOf(f)} />
            </span>
            <span className="k-lr-t">
              <b>{f.name}</b>
            </span>
            {blind ? null : <span className="k-lr-v">{n(f.id)}</span>}
          </button>
          <button type="button" className="k-ib sa-tg" aria-expanded={unfolded} aria-label={`${unfolded ? "Close" : "Open"} ${f.name}`} onClick={() => fold.toggleKid(f.id)}>
            <Icon name="down" />
          </button>
        </div>
      </li>,
      ...(unfolded ? kids.map((k) => row(k, 1)) : []),
    ];
  };
  const section = (g: GroupKey, rows: ReactNode[]) => {
    const unfolded = fold.isOpen(g);
    return (
      <section key={g} className="sa-group">
        <h2 className="sa-gh">
          <button type="button" className="sa-gb" aria-expanded={unfolded} onClick={() => fold.toggle(g)}>
            {g === "Folders" ? null : <GroupMark group={g} />}
            <span>{g}</span>
            <Icon name="down" />
          </button>
        </h2>
        {unfolded ? <GroupedList>{rows}</GroupedList> : null}
      </section>
    );
  };
  const own = tree(null, 0);
  const folderRows = [
    ...own,
    ...(onNewFolder ? [<ListRow key="new" icon="plus" title="New folder" onClick={onNewFolder} noChevron />] : []),
  ];
  const kut = groupFolders(folders, "Flashkut").filter((f) => shows(f.id));
  const rooms = groupFolders(folders, "Flashroom").filter((f) => shows(f.id));
  return (
    <>
      <GroupedList>
        <Row icon="grid" name="All files" count={said(ALL)} on={open === ALL} onOpen={() => onOpen(ALL)} />
        {trash ? <Row icon="trash" name="Trash" count={blind ? null : trash.count} on={open === TRASH} onOpen={trash.onOpen} /> : null}
      </GroupedList>
      {folderRows.length ? section("Folders", folderRows) : null}
      {kut.length ? section("Flashkut", kut.map((f) => row(f))) : null}
      {rooms.length ? section("Flashroom", rooms.flatMap(roomRows)) : null}
    </>
  );
}
